// --- useAuction Hook: Real-time Auction State Sync ---
import { useState, useEffect, useCallback, useRef } from 'react';
import { doc, onSnapshot, setDoc, updateDoc } from 'firebase/firestore';
import { db, APP_ID } from '../config/firebase';
import type { FirestoreAuctionState, AuctionState, Player, Team, AppUser, AuctionSettings } from '../types';
import { useToast } from '../contexts/ToastContext';
import { formatCurrency } from '../utils/helpers';

const AUCTION_DOC_PATH = `artifacts/${APP_ID}/public/data/auction/current`;

// Default Firestore state
const DEFAULT_FIRESTORE_STATE: Omit<FirestoreAuctionState, 'updatedAt'> = {
  isActive: false,
  status: 'idle',
  showCurrentBid: false,
  currentPlayerId: null,
  currentPlayerName: null,
  currentPlayerPhoto: null,
  currentPlayerGender: null,
  currentPlayerSide: null,
  currentBid: 0,
  currentBidderId: null,
  currentBidderName: null,
  basePrice: 100,
  bidIncrement: 50,
  timerDuration: 30,
  timerEndsAt: null,
  startedAt: null,
  conductedBy: '',
  conductedByName: '',
  playerQueue: [],
  season: 'season-3'
};

interface UseAuctionProps {
  players: Player[];
  teams: Team[];
  currentUser: AppUser | null;
  auctionSettings?: AuctionSettings | null; // Optional: Pass settings for enforcement
  onPlayerSold?: (player: Player, teamId: string, price: number) => Promise<void>;
}

interface UseAuctionReturn {
  auction: AuctionState;
  firestoreState: FirestoreAuctionState | null;
  isLoading: boolean;
  // Admin controls
  startAuction: (settings: { basePrice: number; bidIncrement: number; timerDuration: number }) => Promise<void>;
  endAuction: () => Promise<void>;
  updateSettings: (settings: { basePrice?: number; bidIncrement?: number; timerDuration?: number }) => Promise<void>;
  setShowCurrentBid: (visible: boolean) => Promise<void>;
  selectPlayer: (player: Player) => Promise<void>;
  startBidding: () => Promise<void>;
  pauseBidding: () => Promise<void>;
  resumeBidding: () => Promise<void>;
  skipPlayer: () => Promise<void>;
  finalizeSale: () => Promise<void>;
  manualSell: (player: Player, teamId: string, price: number) => Promise<void>;
  markUnsold: () => Promise<void>;
  adminPlaceBid: (teamId: string, customAmount?: number) => Promise<void>;
  // Owner controls
  placeBid: (customAmount?: number) => Promise<boolean>;
  // Helpers
  canBid: boolean;
  myTeam: Team | null;
}

export const useAuction = ({ players, teams, currentUser, auctionSettings, onPlayerSold }: UseAuctionProps): UseAuctionReturn => {
  const toast = useToast();
  const [firestoreState, setFirestoreState] = useState<FirestoreAuctionState | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [localTimer, setLocalTimer] = useState(0);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  // Get current user's team (for owners)
  const myTeam = currentUser?.teamId
    ? teams.find(t => t.id === currentUser.teamId) || null
    : null;

  // Subscribe to auction state
  useEffect(() => {
    const auctionRef = doc(db, AUCTION_DOC_PATH);

    const unsubscribe = onSnapshot(auctionRef, (snapshot) => {
      if (snapshot.exists()) {
        setFirestoreState(snapshot.data() as FirestoreAuctionState);
      } else {
        setFirestoreState(null);
      }
      setIsLoading(false);
    }, (error) => {
      console.error('Auction subscription error:', error);
      // Only toast if user is logged in, to avoid spamming public display on network blip
      if (currentUser) toast.error('Failed to connect to auction');
      setIsLoading(false);
    });

    return () => unsubscribe();
  }, [toast, currentUser]);

  // Timer countdown effect
  useEffect(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    if (firestoreState?.timerEndsAt && firestoreState.status === 'bidding') {
      const updateTimer = () => {
        const remaining = Math.max(0, Math.ceil((firestoreState.timerEndsAt! - Date.now()) / 1000));
        setLocalTimer(remaining);

        if (remaining <= 0 && timerRef.current) {
          clearInterval(timerRef.current);
          timerRef.current = null;
        }
      };

      updateTimer();
      timerRef.current = setInterval(updateTimer, 100);
    } else {
      setLocalTimer(firestoreState?.timerDuration || 30);
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [firestoreState?.timerEndsAt, firestoreState?.status, firestoreState?.timerDuration]);

  // Derive local auction state from Firestore + players/teams
  const auction: AuctionState = {
    isActive: firestoreState?.isActive || false,
    showCurrentBid: firestoreState?.showCurrentBid ?? false,
    status: firestoreState?.status || 'idle',
    currentPlayer: firestoreState?.currentPlayerId
      ? players.find(p => p.id === firestoreState.currentPlayerId) || null
      : null,
    currentBid: firestoreState?.currentBid || 0,
    currentBidder: firestoreState?.currentBidderId
      ? teams.find(t => t.id === firestoreState.currentBidderId) || null
      : null,
    basePrice: firestoreState?.basePrice || 100,
    bidIncrement: firestoreState?.bidIncrement || 50,
    startedAt: firestoreState?.startedAt || null,
    timer: localTimer,
    timerEndsAt: firestoreState?.timerEndsAt || null,
    playerQueue: (firestoreState?.playerQueue || [])
      .map(id => players.find(p => p.id === id))
      .filter((p): p is Player => p !== undefined),
    conductedBy: firestoreState?.conductedBy || '',
    conductedByName: firestoreState?.conductedByName || ''
  };

  // Helper function to check squad limits
  const checkSquadLimit = useCallback((teamId: string, player: Player): { allowed: boolean; reason?: string } => {
    if (!auctionSettings) return { allowed: true };
    const maxMale = auctionSettings.requiredMalePlayers || 0;
    const maxFemale = auctionSettings.requiredFemalePlayers || 0;

    // If limits are 0, no limit enforced
    if (maxMale === 0 && maxFemale === 0) return { allowed: true };

    const gender = player.details?.player_gender?.toLowerCase() || '';
    if (!gender) return { allowed: true }; // Unknown gender

    const teamSquad = players.filter(p => p.teamId === teamId && p.status === 'sold');

    if (gender === 'male' && maxMale > 0) {
      const maleCount = teamSquad.filter(p => p.details?.player_gender?.toLowerCase() === 'male').length;
      if (maleCount >= maxMale) return { allowed: false, reason: `Max male players (${maxMale}) reached` };
    } else if (gender === 'female' && maxFemale > 0) {
      const femaleCount = teamSquad.filter(p => p.details?.player_gender?.toLowerCase() === 'female').length;
      if (femaleCount >= maxFemale) return { allowed: false, reason: `Max female players (${maxFemale}) reached` };
    }

    return { allowed: true };
  }, [auctionSettings, players]);

  const calculateReserveRequirement = useCallback((team: Team, includeCurrentPlayer: boolean) => {
    const totalRequiredPlayers = (auctionSettings?.requiredMalePlayers || 0) + (auctionSettings?.requiredFemalePlayers || 0);
    if (totalRequiredPlayers <= 0) return { requiredReserve: 0, remainingSlots: 0 };

    const ownedCount = players.filter(p => p.teamId === team.id && p.status === 'sold').length;
    const projectedOwned = includeCurrentPlayer && ownedCount < totalRequiredPlayers ? ownedCount + 1 : ownedCount;
    const remainingSlots = Math.max(totalRequiredPlayers - projectedOwned, 0);
    const basePrice = firestoreState?.basePrice || auctionSettings?.minBidPoints || 0;
    const requiredReserve = remainingSlots * basePrice;

    return { requiredReserve, remainingSlots };
  }, [auctionSettings, firestoreState?.basePrice, players]);

  // Check if current user can place a bid
  const canBid = (() => {
    if (!(currentUser?.role === 'owner' && myTeam && firestoreState?.status === 'bidding' && firestoreState.currentPlayerId)) {
      return false;
    }

    const requiredBid = firestoreState.currentBidderId
      ? firestoreState.currentBid + firestoreState.bidIncrement
      : firestoreState.currentBid;

    if (requiredBid <= 0) return false;
    if (firestoreState.currentBidderId === myTeam.id) return false;
    if (myTeam.currentBalance < requiredBid) return false;

    const { requiredReserve } = calculateReserveRequirement(myTeam, true);
    return myTeam.currentBalance - requiredBid >= requiredReserve;
  })();

  // Helper to update auction doc
  const updateAuction = async (updates: Partial<FirestoreAuctionState>) => {
    const auctionRef = doc(db, AUCTION_DOC_PATH);
    await updateDoc(auctionRef, { ...updates, updatedAt: new Date().toISOString() });
  };

  // === ADMIN CONTROLS ===

  // Start a new auction session
  const startAuction = useCallback(async (settings: { basePrice: number; bidIncrement: number; timerDuration: number }) => {
    if (!currentUser || !['admin', 'sub_admin'].includes(currentUser.role)) {
      toast.error('Only admins can start auctions');
      return;
    }

    const auctionRef = doc(db, AUCTION_DOC_PATH);
    const unsoldPlayers = players.filter(p => p.status === 'unsold').map(p => p.id);

    await setDoc(auctionRef, {
      ...DEFAULT_FIRESTORE_STATE,
      isActive: true,
      status: 'idle',
      basePrice: settings.basePrice,
      bidIncrement: settings.bidIncrement,
      timerDuration: settings.timerDuration,
      playerQueue: unsoldPlayers,
      startedAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      conductedBy: currentUser.uid,
      conductedByName: currentUser.name
    });

    toast.success('Auction started!');
  }, [currentUser, players, toast]);

  // End the auction session
  const endAuction = useCallback(async () => {
    if (!currentUser || !['admin', 'sub_admin'].includes(currentUser.role)) return;

    await updateAuction({
      isActive: false,
      status: 'idle',
      currentPlayerId: null,
      currentPlayerName: null,
      currentBid: 0,
      currentBidderId: null,
      currentBidderName: null,
      timerEndsAt: null
    });

    toast.info('Auction ended');
  }, [currentUser, toast]);

  // Update auction settings (can be done during active auction)
  const updateSettings = useCallback(async (settings: { basePrice?: number; bidIncrement?: number; timerDuration?: number }) => {
    if (!currentUser || !['admin', 'sub_admin'].includes(currentUser.role)) {
      toast.error('Only admins can update settings');
      return;
    }

    const updates: Partial<FirestoreAuctionState> = {};
    if (settings.basePrice !== undefined) updates.basePrice = settings.basePrice;
    if (settings.bidIncrement !== undefined) updates.bidIncrement = settings.bidIncrement;
    if (settings.timerDuration !== undefined) updates.timerDuration = settings.timerDuration;

    await updateAuction(updates);
    toast.success('Auction settings updated');
  }, [currentUser, toast]);

  const setShowCurrentBid = useCallback(async (visible: boolean) => {
    if (!currentUser || !['admin', 'sub_admin'].includes(currentUser.role)) {
      toast.error('Only admins can update public display');
      return;
    }
    if (!firestoreState) return;

    await updateAuction({ showCurrentBid: visible });
  }, [currentUser, toast, firestoreState]);

  // Select a player for auction
  const selectPlayer = useCallback(async (player: Player) => {
    if (!currentUser || !['admin', 'sub_admin'].includes(currentUser.role)) return;
    if (!firestoreState?.isActive) return;

    await updateAuction({
      status: 'idle',
      currentPlayerId: player.id,
      currentPlayerName: player.details?.player_full_name || player.name,
      currentPlayerPhoto: player.details?.photo_url || null,
      currentPlayerGender: player.details?.player_gender || null,
      currentPlayerSide: player.details?.playing_side || null,
      currentBid: firestoreState.basePrice,
      currentBidderId: null,
      currentBidderName: null,
      timerEndsAt: null
    });
  }, [currentUser, firestoreState]);

  // Start bidding on current player
  const startBidding = useCallback(async () => {
    if (!currentUser || !['admin', 'sub_admin'].includes(currentUser.role)) return;
    if (!firestoreState?.currentPlayerId) return;

    const timerEndsAt = Date.now() + (firestoreState.timerDuration * 1000);

    await updateAuction({
      status: 'bidding',
      timerEndsAt
    });

    toast.success('Bidding started!');
  }, [currentUser, firestoreState, toast]);

  // Pause bidding
  const pauseBidding = useCallback(async () => {
    if (!currentUser || !['admin', 'sub_admin'].includes(currentUser.role)) return;

    await updateAuction({
      status: 'paused',
      timerEndsAt: null
    });
  }, [currentUser]);

  // Resume bidding
  const resumeBidding = useCallback(async () => {
    if (!currentUser || !['admin', 'sub_admin'].includes(currentUser.role)) return;
    if (!firestoreState) return;

    const timerEndsAt = Date.now() + (localTimer * 1000);

    await updateAuction({
      status: 'bidding',
      timerEndsAt
    });
  }, [currentUser, firestoreState, localTimer]);

  // Skip current player
  const skipPlayer = useCallback(async () => {
    if (!currentUser || !['admin', 'sub_admin'].includes(currentUser.role)) return;

    await updateAuction({
      status: 'idle',
      currentPlayerId: null,
      currentPlayerName: null,
      currentPlayerPhoto: null,
      currentBid: firestoreState?.basePrice || 100,
      currentBidderId: null,
      currentBidderName: null,
      timerEndsAt: null
    });

    toast.info('Player skipped');
  }, [currentUser, firestoreState, toast]);

  // Finalize sale to current bidder
  const finalizeSale = useCallback(async () => {
    if (!currentUser || !['admin', 'sub_admin'].includes(currentUser.role)) return;
    if (!firestoreState?.currentPlayerId || !firestoreState.currentBidderId) {
      toast.error('No valid bidder');
      return;
    }

    const player = players.find(p => p.id === firestoreState.currentPlayerId);
    if (!player) return;

    // Double check squad limit for admin manual "Sell Now" (though owners are blocked from bidding)
    const limitCheck = checkSquadLimit(firestoreState.currentBidderId, player);
    if (!limitCheck.allowed) {
      toast.error(`Cannot sell: ${limitCheck.reason}`);
      return;
    }

    const biddingTeam = teams.find(t => t.id === firestoreState.currentBidderId);
    if (biddingTeam) {
      const { requiredReserve } = calculateReserveRequirement(biddingTeam, true);
      if (biddingTeam.currentBalance - firestoreState.currentBid < requiredReserve) {
        toast.error(`Reserve at least ${formatCurrency(requiredReserve)} pts for remaining picks`);
        return;
      }
    }

    // Call the onPlayerSold callback to handle the actual sale
    if (onPlayerSold) {
      await onPlayerSold(player, firestoreState.currentBidderId, firestoreState.currentBid);
    }

    // Remove from queue and reset current player
    const newQueue = (firestoreState.playerQueue || []).filter(id => id !== player.id);

    await updateAuction({
      status: 'sold',
      playerQueue: newQueue,
      timerEndsAt: null
    });

    toast.success(`${player.name} sold for ${formatCurrency(firestoreState.currentBid)} points!`);

    // Auto-reset after 2 seconds
    setTimeout(async () => {
      await updateAuction({
        status: 'idle',
        currentPlayerId: null,
        currentPlayerName: null,
        currentPlayerPhoto: null,
        currentBid: firestoreState.basePrice,
        currentBidderId: null,
        currentBidderName: null
      });
    }, 2000);
  }, [currentUser, firestoreState, players, onPlayerSold, toast, checkSquadLimit, calculateReserveRequirement, teams]);

  // Manual/Offline Sale (Admin override)
  const manualSell = useCallback(async (player: Player, teamId: string, price: number) => {
    if (!currentUser || !['admin', 'sub_admin'].includes(currentUser.role)) return;

    // Check limits
    const limitCheck = checkSquadLimit(teamId, player);
    if (!limitCheck.allowed) {
       toast.error(`Cannot sell: ${limitCheck.reason}`);
       return;
    }

    const team = teams.find(t => t.id === teamId);
    if (!team) return;

    // Check balance
    if (team.currentBalance < price) {
      toast.error(`Team has insufficient balance (${formatCurrency(team.currentBalance)})`);
      return;
    }

    const { requiredReserve } = calculateReserveRequirement(team, true);
    if (team.currentBalance - price < requiredReserve) {
      toast.error(`Reserve at least ${formatCurrency(requiredReserve)} pts for remaining picks`);
      return;
    }

    if (onPlayerSold) {
      await onPlayerSold(player, teamId, price);
    }

    // Remove from queue
    const newQueue = (firestoreState?.playerQueue || []).filter(id => id !== player.id);

    // Update auction state to reflect sale
    await updateAuction({
      status: 'sold',
      currentPlayerId: player.id,
      currentPlayerName: player.details?.player_full_name || player.name,
      currentPlayerPhoto: player.details?.photo_url || null,
      currentBid: price,
      currentBidderId: teamId,
      currentBidderName: team.name,
      playerQueue: newQueue,
      timerEndsAt: null
    });

    toast.success(`Sold manually to ${team.name} for ${formatCurrency(price)}!`);

     // Auto-reset after 2 seconds
    setTimeout(async () => {
      await updateAuction({
        status: 'idle',
        currentPlayerId: null,
        currentPlayerName: null,
        currentPlayerPhoto: null,
        currentBid: firestoreState?.basePrice || 100,
        currentBidderId: null,
        currentBidderName: null
      });
    }, 2000);
  }, [currentUser, firestoreState, teams, onPlayerSold, toast, checkSquadLimit, calculateReserveRequirement]);

  // Admin place bid (Proxy Bidding)
  const adminPlaceBid = useCallback(async (teamId: string, customAmount?: number) => {
    if (!currentUser || !['admin', 'sub_admin'].includes(currentUser.role)) return;
    if (!firestoreState?.isActive || !firestoreState.currentPlayerId) return;

    const team = teams.find(t => t.id === teamId);
    if (!team) return;

    // Check squad limits
    const player = players.find(p => p.id === firestoreState.currentPlayerId);
    if (player) {
      const limitCheck = checkSquadLimit(teamId, player);
      if (!limitCheck.allowed) {
        toast.error(`Cannot bid: ${limitCheck.reason}`);
        return;
      }
    }

    let newBid: number;

    const minimumBid = firestoreState.currentBidderId
      ? firestoreState.currentBid + firestoreState.bidIncrement
      : firestoreState.currentBid;

    if (customAmount !== undefined) {
      // Validate custom amount
      if (customAmount < minimumBid) {
        toast.error(`Bid must be at least ${formatCurrency(minimumBid)}`);
        return;
      }
      if (customAmount !== firestoreState.currentBid && customAmount % firestoreState.bidIncrement !== 0) {
        toast.error(`Bid must be multiple of ${formatCurrency(firestoreState.bidIncrement)}`);
        return;
      }
      newBid = customAmount;
    } else {
      newBid = minimumBid;
    }

    if (team.currentBalance < newBid) {
      toast.error('Team has insufficient balance');
      return;
    }

    const { requiredReserve } = calculateReserveRequirement(team, true);
    if (team.currentBalance - newBid < requiredReserve) {
      toast.error(`Reserve at least ${formatCurrency(requiredReserve)} pts for remaining picks`);
      return;
    }

    // Reset timer
    const timerEndsAt = Date.now() + (firestoreState.timerDuration * 1000);

    await updateAuction({
      status: 'bidding',
      currentBid: newBid,
      currentBidderId: team.id,
      currentBidderName: team.name,
      timerEndsAt
    });

    toast.success(`Proxy bid: ${formatCurrency(newBid)} for ${team.name}`);
  }, [currentUser, firestoreState, teams, players, toast, checkSquadLimit, calculateReserveRequirement]);

  // Mark player as unsold
  const markUnsold = useCallback(async () => {
    if (!currentUser || !['admin', 'sub_admin'].includes(currentUser.role)) return;

    await updateAuction({
      status: 'unsold',
      timerEndsAt: null
    });

    toast.info('Player marked unsold');

    // Auto-reset after 2 seconds
    setTimeout(async () => {
      await updateAuction({
        status: 'idle',
        currentPlayerId: null,
        currentPlayerName: null,
        currentBid: firestoreState?.basePrice || 100,
        currentBidderId: null,
        currentBidderName: null
      });
    }, 2000);
  }, [currentUser, firestoreState, toast]);

  // === OWNER CONTROLS ===

  // Place a bid
  const placeBid = useCallback(async (customAmount?: number): Promise<boolean> => {
    if (!canBid || !myTeam || !firestoreState) {
      toast.error('Cannot place bid');
      return false;
    }

    // Check squad limits for owner
    if (auction.currentPlayer) {
      const limitCheck = checkSquadLimit(myTeam.id, auction.currentPlayer);
      if (!limitCheck.allowed) {
        toast.error(limitCheck.reason || 'Squad limit reached');
        return false;
      }
    }

    let newBid: number;

    const minimumBid = firestoreState.currentBidderId
      ? firestoreState.currentBid + firestoreState.bidIncrement
      : firestoreState.currentBid;

    if (customAmount !== undefined) {
      // Custom Bid Validation
      if (customAmount < minimumBid) {
        toast.error(`Bid must be at least ${formatCurrency(minimumBid)}`);
        return false;
      }

      if (customAmount !== firestoreState.currentBid && customAmount % firestoreState.bidIncrement !== 0) {
        toast.error(`Bid must be a multiple of ${formatCurrency(firestoreState.bidIncrement)}`);
        return false;
      }

      if (auctionSettings?.maxBidPoints && customAmount > auctionSettings.maxBidPoints) {
        toast.error(`Bid cannot exceed ${formatCurrency(auctionSettings.maxBidPoints)}`);
        return false;
      }

      newBid = customAmount;
    } else {
      // Standard increment
      newBid = minimumBid;
    }

    if (myTeam.currentBalance < newBid) {
      toast.error('Insufficient balance');
      return false;
    }

    const { requiredReserve } = calculateReserveRequirement(myTeam, true);
    if (myTeam.currentBalance - newBid < requiredReserve) {
      toast.error(`Reserve at least ${formatCurrency(requiredReserve)} pts for remaining picks`);
      return false;
    }

    // Reset timer on new bid
    const timerEndsAt = Date.now() + (firestoreState.timerDuration * 1000);

    await updateAuction({
      currentBid: newBid,
      currentBidderId: myTeam.id,
      currentBidderName: myTeam.name,
      timerEndsAt
    });

    toast.success(`Bid placed: ${formatCurrency(newBid)} points`);
    return true;
  }, [canBid, myTeam, firestoreState, toast, checkSquadLimit, auction.currentPlayer, auctionSettings, calculateReserveRequirement]);

  return {
    auction,
    firestoreState,
    isLoading,
    startAuction,
    endAuction,
    updateSettings,
    setShowCurrentBid,
    selectPlayer,
    startBidding,
    pauseBidding,
    resumeBidding,
    skipPlayer,
    finalizeSale,
    manualSell,
    markUnsold,
    adminPlaceBid,
    placeBid,
    canBid,
    myTeam
  };
};
